import { useDispatch } from 'react-redux';
import { DestructiveButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { addPositiveToast } from 'modules/toastContainer';
import { deleteNodes, deleteEdges, closeModal } from '../../../ducks';
import { DELETE_NODE_MODAL } from '../../../constants';
import { Node, Edge } from '../../../types';
import {
  ChartEntity,
  NodeAction,
  createContentFor,
  generateToastText,
  generateBody,
  generateTitle,
} from '../utils';

type Props = {
  /**  Nodes to delete */
  nodesToDelete: Node[];
  /**  Edges to delete */
  edgesToDelete: Edge[];
  /** Whether the modal is open */
  isOpen: boolean;
};

const getChartEntityType = (nodeList: Node[]) =>
  nodeList.length > 0 ? ChartEntity.NODE : ChartEntity.EDGE;

export const DeleteNodeModal = ({ nodesToDelete, edgesToDelete, isOpen }: Props) => {
  const itemType = getChartEntityType(nodesToDelete);
  const itemsToDelete = itemType === ChartEntity.NODE ? nodesToDelete : edgesToDelete;

  const dispatch = useDispatch();

  const handleCloseModal = () => dispatch(closeModal(DELETE_NODE_MODAL));

  const handleSubmit = () => {
    dispatch(deleteEdges(edgesToDelete));
    dispatch(deleteNodes(nodesToDelete.map((n) => n.id)));
    dispatch(addPositiveToast(generateToastText(NodeAction.Delete, itemType, itemsToDelete)));
    handleCloseModal();
  };

  return (
    <Modal
      id={DELETE_NODE_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>
        {createContentFor(generateTitle, itemsToDelete, itemType, NodeAction.Delete)}?
      </ModalHeader>
      <ModalBody>
        {createContentFor(generateBody, itemsToDelete, itemType, NodeAction.Delete)}
        {` This cannot be undone.`}
      </ModalBody>
      <ModalFooter>
        <DestructiveButton autoFocus onClick={handleSubmit} size={BUTTON_SIZES.LARGE}>
          Delete {itemType}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
